import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Logo } from '../components/Logo';
import { 
  CheckCircle2, 
  Package, 
  Truck,
  ArrowRight,
  Loader2,
  Play,
  Calendar
} from 'lucide-react';

const OrderSuccessPage = () => {
  const { api } = useAuth();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!orderId) {
        setLoading(false);
        return;
      }

      try {
        // Poll for order status
        let attempts = 0;
        const maxAttempts = 10;
        
        while (attempts < maxAttempts) {
          const response = await api.get(`/orders/${orderId}/status`);
          if (response.data.status !== 'pending') {
            setOrder(response.data);
            break;
          }
          await new Promise(resolve => setTimeout(resolve, 2000));
          attempts++;
        }
        
        if (!order) {
          const response = await api.get(`/orders/${orderId}`);
          setOrder(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch order:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, api]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#090D0F] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-400 mx-auto mb-4" />
          <p className="text-zinc-400">Processing your order...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#090D0F]">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Logo linkTo="/" size="default" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        {/* Success Icon */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-teal-400 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-black" />
          </div>
          <h1 className="text-4xl font-bold mb-4">ORDER CONFIRMED!</h1>
          <p className="text-zinc-400 text-lg">
            Thank you for your purchase. Your Lagree journey begins now.
          </p>
          {orderId && (
            <p className="text-zinc-600 mt-2">Order #{orderId.slice(0, 8).toUpperCase()}</p>
          )}
        </div>

        {/* Order Summary */}
        {order && (
          <div className="bg-[#121416] border border-zinc-800 rounded-sm p-8 mb-8">
            <h2 className="text-xl font-bold mb-6">ORDER DETAILS</h2>
            
            <div className="space-y-4 mb-6">
              {order.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{item.name}</p>
                    <p className="text-sm text-zinc-500">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-semibold">${item.price.toLocaleString()}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-zinc-700 pt-4">
              <div className="flex justify-between text-lg font-bold">
                <span>Total Paid</span>
                <span className="text-teal-400">${order.total?.toLocaleString()}</span>
              </div>
            </div>
          </div>
        )}

        {/* What's Next */}
        <div className="bg-[#121416] border border-zinc-800 rounded-sm p-8 mb-8">
          <h2 className="text-xl font-bold mb-6">WHAT'S NEXT?</h2>
          
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-teal-400/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <h3 className="font-bold mb-1">Order Processing</h3>
                <p className="text-zinc-400 text-sm">
                  Your order is being prepared. You'll receive an email confirmation shortly.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-teal-400/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <h3 className="font-bold mb-1">Shipping (5-7 Business Days)</h3>
                <p className="text-zinc-400 text-sm">
                  We'll send tracking information once your machine ships. Free white-glove delivery included.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-teal-400/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-teal-400" />
              </div>
              <div>
                <h3 className="font-bold mb-1">Setup & Onboarding</h3>
                <p className="text-zinc-400 text-sm">
                  Once delivered, our team will reach out to schedule your personal setup and onboarding session.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Start Classes CTA */}
        <div className="bg-teal-400/10 border border-teal-400/30 rounded-sm p-8 text-center">
          <Play className="w-12 h-12 text-teal-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold mb-2">Start Exploring Classes</h3>
          <p className="text-zinc-400 mb-6">
            While you wait for your machine, start browsing our class library and get familiar with the Lagree Method.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/classes">
              <Button className="bg-teal-400 text-black font-bold uppercase hover:bg-teal-300">
                Browse Classes
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/dashboard">
              <Button variant="outline" className="border-zinc-700 hover:bg-zinc-800">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        </div>

        {/* Support */}
        <div className="text-center mt-12">
          <p className="text-zinc-500">
            Questions about your order? Contact us at{' '}
            <a href="mailto:support@lagreemvmt.com" className="text-teal-400 hover:underline">
              support@lagreemvmt.com
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default OrderSuccessPage;
